import { useContext, useEffect, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import { Helmet } from "react-helmet-async";
import { BiPlus } from "react-icons/bi";
import { useOutletContext } from "react-router";
import API from "../../../utils/API";
import AlertModal from "../../../utils/components/AlertModal";
import Input from "../../../utils/components/Input";
import Modal from "../../../utils/components/Modal";
import { useProject } from "../../../utils/hooks/useProject";
import Sets from "./Sets";
import { RequestContext } from "../../../utils/context/RequestContext";

const StatusSets = () => {
  const [statusSets, setStatusSets] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [statusSetName, setStatusSetName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const { project } = useProject();

  const [translate] = useOutletContext();

  const { setRequestLoading, requestLoading } = useContext(RequestContext);

  useEffect(() => {
    if (project?.id) {
      API.getData(`status_sets?project_id=${project.id}`).then(
        ({ data: res }) => {
          setTimeout(() => {
            const data = res.data.map((item) => {
              return {
                id: item.id,
                title: item.name,
                subTree: item.details
                  ? item.details.map((detail) => ({
                      ...detail,
                      roles: detail.roles || [],
                    }))
                  : [],
                auto_change: item.auto_change,
              };
            });

            setStatusSets(data);
          }, 20);

          setTimeout(() => {
            setIsLoading(false);
          }, 50);
        }
      );

      API.getData(`roles?project_id=${project.id}`).then(({ data: res }) => {
        setTimeout(() => {
          const data = res.data.map((item) => {
            return {
              id: item.id,
              title: item.name,
            };
          });

          setRoles(data);
        }, 20);
      });
    } else {
      setIsLoading(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (requestLoading) return;

    if (!statusSetName.trim() || !project) {
      confirmAlert({
        customUI: ({ onClose }) => (
          <AlertModal
            buttons={[
              {
                label: translate("okay"),
                onClick: onClose,
                classNames: "btn btn-primary",
              },
            ]}
            message={translate("fill_all_fields")}
          />
        ),
      });
      return false;
    }

    const body = {
      name: statusSetName.trim(),
      project_id: project.id,
    };

    setRequestLoading(true);

    API.postData(`status_sets`, body).then(({ data: res }) => {
      const { data } = res;

      const newItem = {
        id: data.id,
        title: data.name,
        subTree: [],
        auto_change: false,
      };

      setStatusSets([...statusSets, newItem]);

      setShowModal(false);
      setStatusSetName("");
    });
  };

  const handleUpdate = (id, body) => {
    if (isUpdating) return false;

    setIsUpdating(true);
    API.putData(`status_set/${id}`, body)
      .then(({ data: res }) => {
        const [data] = res.data;

        const newItem = {
          id: data.id,
          title: data.name,
          subTree: data.details,
          auto_change: data.auto_change,
        };

        const newSets = statusSets.map((item) => {
          if (item.id === id) {
            return newItem;
          }

          return item;
        });

        setStatusSets(newSets);

        setIsUpdating(false);
      })
      .catch(() => setIsUpdating(false));
  };

  const onDelete = (id) => {
    if (!id) return false;

    confirmAlert({
      customUI: ({ onClose }) => (
        <AlertModal
          buttons={[
            {
              label: translate("cancel"),
              onClick: onClose,
              classNames: "btn btn-secondary",
            },
            {
              label: translate("delete"),
              onClick: () => {
                handleDelete(id, onClose);
              },
              classNames: "btn btn-danger",
            },
          ]}
          message={translate("delete_confirmation")}
        />
      ),
    });
  };

  const handleDelete = (id, closeModal) => {
    if (requestLoading) return;
    setRequestLoading(true);
    API.deleteData(`status_set/${id}`).then(() => {
      const newRevisionSets = statusSets.filter((item) => item.id !== id);
      setStatusSets(newRevisionSets);
      closeModal();
    });
  };

  const handleAddDetail = (parent_id, body, callback = null) => {
    if (requestLoading) return;
    if (parent_id && body) {
      if (!body.name.trim()) {
        confirmAlert({
          customUI: ({ onClose }) => (
            <AlertModal
              buttons={[
                {
                  label: translate("okay"),
                  onClick: onClose,
                  classNames: "btn btn-primary",
                },
              ]}
              message={translate("fill_all_fields")}
            />
          ),
        });
        return false;
      }

      setRequestLoading(true);

      body = {
        name: body.name.trim(),
      };
      API.postData(`status_set/${parent_id}/details`, body).then(
        ({ data: res }) => {
          const { data } = res;

          const newItem = {
            id: data.id,
            name: data.name,
            set_id: parent_id,
            roles: [],
          };

          const sets = [...statusSets];

          const index = sets.findIndex((item) => item.id === parent_id);

          if (index > -1) {
            sets[index].subTree = [...sets[index].subTree, newItem];
            setStatusSets(sets);
          }

          if (callback) callback(newItem);
        }
      );
    }
  };

  return (
    <div className="StatusSets SetsPage">
      <Helmet>
        <title>
          {translate("page_titles.project-settings-status-sets")} -{" "}
          {translate("page_titles.project-settings")}
        </title>
      </Helmet>
      <div className="row">
        <div className="col-12 d-flex">
          <div
            className="d-flex align-items-center mb-4"
            role="button"
            onClick={() =>
              project?.id
                ? setShowModal(true)
                : alert(translate("select_a_project_alert"))
            }
          >
            <BiPlus className="fs-1 bg-primary text-white rounded-circle" />
            <span className="ms-2">
              {translate(
                "project_settings.revision_sets.create_new_set_button"
              )}
            </span>
          </div>
        </div>
      </div>
      {project?.id ? (
        <>
          <div className="row">
            <div className="col-12">
              <Sets
                sets={statusSets}
                setsLoading={isLoading}
                handleDelete={onDelete}
                handleAddDetail={handleAddDetail}
                handleUpdate={handleUpdate}
                roles={roles}
                type="STATUS_SET"
              />
            </div>
          </div>

          <Modal
            title={translate("project_settings.revision_sets.create_new_set")}
            positiveButtonNode={translate("save")}
            negativeButtonNode={translate("cancel")}
            positiveButtonHandler={handleSubmit}
            modalSize="lg"
            show={showModal}
            setShow={(show) => {
              setShowModal(show);
            }}
          >
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <Input
                  type="text"
                  name="statusSetName"
                  title={translate("project_settings.revision_sets.set_name")}
                  placeholder={translate(
                    "project_settings.revision_sets.set_name_placeholder"
                  )}
                  value={statusSetName}
                  onChange={(e) => setStatusSetName(e.target.value)}
                />
              </div>
            </form>
          </Modal>
        </>
      ) : (
        ``
      )}
    </div>
  );
};

export default StatusSets;
